import {
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { supabase } from "@project/lib/data-client";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@project/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { Badge } from "@project/components/ui/badge";
import { Switch } from "@project/components/ui/switch";
import { TagsInput } from "@project/admin/components/tags-input";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { exportApiRowsToCsv } from "@project/lib/csv";
import { formatDate } from "@project/lib/date-format";
import { CsvExportButton } from "@project/admin/components/csv-export-button";

export type Field = {
  name: string;
  label: string;
  type?:
    | "text"
    | "email"
    | "number"
    | "date"
    | "time"
    | "textarea"
    | "select"
    | "tel"
    | "url"
    | "boolean"
    | "tags"
    | "rating"
    | "image";
  options?: { value: string; label: string }[];
  required?: boolean;
  placeholder?: string;
  hideInTable?: boolean;
  showInTable?: boolean;
  /** When true the field is not rendered in the form or table; useful for auto-derived columns like slug. */
  hidden?: boolean;
  /** Auto-generate value from this other field (slugified) on save when empty. */
  autoSlugFrom?: string;
  /** When this field changes, auto-fill another field using valueMap (e.g. name → code). */
  linkedField?: { target: string; valueMap: Record<string, string> };
  /** Read-only in the form (still shown in table unless hidden). */
  readOnly?: boolean;
  /** Format numeric values as INR in the table. */
  currency?: boolean;
  help?: string;
  /** When set, this field is rendered as a searchable Select fed by the lookup table,
   *  and the table cell shows the looked-up label (e.g. resident name) instead of the UUID. */
  lookup?: {
    table: string;
    labelField: string;
    valueField?: string;
    secondaryField?: string;
  };
};

function slugify(s: string) {
  return (
    String(s || "")
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80) || `item-${Date.now()}`
  );
}

export function ResourceTable<T extends { id: string; created_at?: string }>({
  table,
  title,
  fields,
  searchFields = [],
  orderBy = "created_at",
  orderAsc = false,
  statusOptions,
  renderRowExtra,
  dateField,
  statusField,
  statusFilterOptions,
  totalField,
}: {
  table: string;
  title: string;
  fields: Field[];
  searchFields?: string[];
  orderBy?: string;
  orderAsc?: boolean;
  statusOptions?: string[];
  renderRowExtra?: (row: T) => ReactNode;
  dateField?: string;
  statusField?: string;
  statusFilterOptions?: string[];
  totalField?: string;
}) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<T> | null>(null);
  const [open, setOpen] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [lookups, setLookups] = useState<
    Record<string, Record<string, { label: string; secondary?: string }>>
  >({});
  const deferredSearch = useDeferredValue(search);

  // Load lookup tables once
  useEffect(() => {
    const lookupFields = fields.filter(f => f.lookup);
    if (lookupFields.length === 0) {
      return;
    }
    (async () => {
      const out: Record<
        string,
        Record<string, { label: string; secondary?: string }>
      > = {};
      for (const f of lookupFields) {
        const lk = f.lookup!;
        const cols = [lk.valueField ?? "id", lk.labelField, lk.secondaryField]
          .filter(Boolean)
          .join(",");
        const { data } = await supabase
          .from(lk.table as any)
          .select(cols)
          .limit(2000);
        const map: Record<string, { label: string; secondary?: string }> = {};
        for (const row of (data as any[] | null) ?? []) {
          const id = String(row[lk.valueField ?? "id"]);
          map[id] = {
            label: String(row[lk.labelField] ?? id),
            secondary: lk.secondaryField
              ? String(row[lk.secondaryField] ?? "")
              : undefined,
          };
        }
        out[f.name] = map;
      }
      setLookups(out);
    })();
    // eslint-disable-next-line
  }, [table]);

  const tableFields = useMemo(() => {
    const visible = fields.filter(f => !f.hidden);
    const explicit = visible.filter(f => f.showInTable);
    return explicit.length > 0
      ? explicit
      : visible.filter(f => !f.hideInTable).slice(0, 5);
  }, [fields]);

  const selectColumns = useMemo(() => {
    const cols = new Set(
      [
        "id",
        "created_at",
        orderBy,
        dateField,
        statusField,
        totalField,
        ...searchFields,
        ...fields.map(f => f.name),
      ].filter(Boolean) as string[]
    );
    return Array.from(cols).join(",");
  }, [dateField, fields, orderBy, searchFields, statusField, totalField]);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from(table as any)
      .select(selectColumns)
      .order(orderBy, { ascending: orderAsc })
      .limit(500);
    if (error) {
      toast.error(error.message);
    } else {
      setRows((data ?? []) as unknown as T[]);
    }
    setLoading(false);
  };
  useEffect(() => {
    void load(); /* eslint-disable-next-line */
  }, [table, selectColumns, orderBy, orderAsc]);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (dateField) {
        const d = String((r as any)[dateField] ?? "").slice(0, 10);
        if (fromDate && d && d < fromDate) {
          return false;
        }
        if (toDate && d && d > toDate) {
          return false;
        }
      }
      if (
        statusField &&
        statusFilter !== "all" &&
        (r as any)[statusField] !== statusFilter
      ) {
        return false;
      }
      if (!deferredSearch.trim() || searchFields.length === 0) {
        return true;
      }
      const q = deferredSearch.toLowerCase();
      return searchFields.some(f => {
        const raw = String((r as any)[f] ?? "").toLowerCase();
        if (raw.includes(q)) {
          return true;
        }
        // Also search inside lookup label (e.g. resident name from resident_id)
        const lk = lookups[f]?.[(r as any)[f]];
        if (lk?.label?.toLowerCase().includes(q)) {
          return true;
        }
        return false;
      });
    });
  }, [
    rows,
    deferredSearch,
    searchFields,
    fromDate,
    toDate,
    statusFilter,
    dateField,
    statusField,
    lookups,
  ]);

  const total = useMemo(
    () =>
      totalField
        ? filtered.reduce((s, r) => s + Number((r as any)[totalField] ?? 0), 0)
        : 0,
    [filtered, totalField]
  );
  const visibleRows = useMemo(() => filtered.slice(0, 100), [filtered]);

  const startNew = () => {
    setEditing({});
    setOpen(true);
  };
  const startEdit = (r: T) => {
    setEditing(r);
    setOpen(true);
  };
  const remove = async (id: string) => {
    if (!confirm("Delete this record?")) {
      return;
    }
    const { error } = await supabase
      .from(table as any)
      .delete()
      .eq("id", id);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Deleted");
      void load();
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) {
      return;
    }
    const payload: any = {};
    for (const f of fields) {
      const v = (editing as any)[f.name];
      if (f.type === "boolean") {
        payload[f.name] = v === true || v === "true";
        continue;
      }
      if (f.type === "tags") {
        if (Array.isArray(v)) {
          payload[f.name] = v;
        } else if (typeof v === "string" && v.trim()) {
          payload[f.name] = v
            .split(",")
            .map(s => s.trim())
            .filter(Boolean)
            .map(s => (isNaN(Number(s)) ? s : Number(s)));
        } else {
          payload[f.name] = [];
        }
        continue;
      }
      if (v === "" || v === undefined) {
        continue;
      }
      if (f.type === "number") {
        payload[f.name] = Number(v);
      } else if (
        typeof v === "string" &&
        /^(suggested_amounts|faq|donation_amount_options)$/.test(f.name)
      ) {
        try {
          payload[f.name] = JSON.parse(v);
        } catch {
          toast.error(`${f.label}: invalid JSON`);
          return;
        }
      } else if (
        typeof v === "string" &&
        /^(is_|requires_|featured_|show_|gateway_)/.test(f.name) &&
        (v === "true" || v === "false")
      ) {
        payload[f.name] = v === "true";
      } else {
        payload[f.name] = v;
      }
    }
    // Auto-fill slug-like fields from a source field when empty
    for (const f of fields) {
      if (
        f.autoSlugFrom &&
        (payload[f.name] === undefined ||
          payload[f.name] === "" ||
          payload[f.name] === null)
      ) {
        const source =
          (editing as any)[f.autoSlugFrom] ?? payload[f.autoSlugFrom];
        if (source) {
          payload[f.name] = slugify(String(source));
        }
      }
    }
    const isUpdate = !!(editing as any).id;
    const q = isUpdate
      ? supabase
          .from(table as any)
          .update(payload)
          .eq("id", (editing as any).id)
      : supabase.from(table as any).insert(payload);
    const { error } = await q;
    if (error) {
      return toast.error(error.message);
    }
    toast.success(isUpdate ? "Updated" : "Created");
    setOpen(false);
    setEditing(null);
    void load();
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-2 sm:flex sm:flex-wrap sm:items-center">
        <div className="relative sm:min-w-[180px] sm:max-w-sm sm:flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={`Search ${searchFields.join(", ") || "records"}...`}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-muted/40 rounded-xl border-transparent pl-9"
          />
        </div>
        {dateField && (
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
            <Input
              type="date"
              value={fromDate}
              onChange={e => setFromDate(e.target.value)}
              className="bg-muted/40 w-full rounded-xl border-transparent sm:w-36"
              title="From"
            />
            <Input
              type="date"
              value={toDate}
              onChange={e => setToDate(e.target.value)}
              className="bg-muted/40 w-full rounded-xl border-transparent sm:w-36"
              title="To"
            />
          </div>
        )}
        {statusField && statusFilterOptions && (
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="bg-muted/40 w-full rounded-xl border-transparent sm:w-40">
              <SelectValue placeholder="All status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All status</SelectItem>
              {statusFilterOptions.map(s => (
                <SelectItem key={s} value={s} className="capitalize">
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {(fromDate || toDate || statusFilter !== "all" || search) && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setFromDate("");
              setToDate("");
              setStatusFilter("all");
              setSearch("");
            }}
            className="rounded-xl"
          >
            Clear
          </Button>
        )}
        {totalField && (
          <div className="text-sm text-muted-foreground">
            Total:{" "}
            <span className="font-bold text-foreground">
              ₹{total.toLocaleString("en-IN")}
            </span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          <CsvExportButton
            className="flex-1 sm:flex-none"
            onExport={() => {
              if (!filtered.length) {
                toast.error("Nothing to export");
                return;
              }
              exportApiRowsToCsv(
                `${table}-${new Date().toISOString().slice(0, 10)}`,
                filtered as Record<string, unknown>[]
              );
              toast.success(`Exported ${filtered.length} rows`);
            }}
          />
          <Dialog
            open={open}
            onOpenChange={v => {
              setOpen(v);
              if (!v) {
                setEditing(null);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                size="sm"
                onClick={startNew}
                className="flex-1 gap-1 rounded-xl sm:flex-none"
              >
                <Plus className="h-4 w-4" /> Add {title}
              </Button>
            </DialogTrigger>
            <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl flex-col gap-0 overflow-hidden rounded-2xl bg-background p-0">
              <DialogHeader className="shrink-0 border-b border-border px-4 py-4 pr-12 text-left sm:px-6">
                <DialogTitle>
                  {(editing as any)?.id ? `Edit ${title}` : `New ${title}`}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={save} className="flex min-h-0 flex-1 flex-col">
                <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
                  <div className="grid gap-3 sm:grid-cols-2">
                    {fields
                      .filter(f => !f.hidden)
                      .map(f => (
                        <div
                          key={f.name}
                          className={
                            f.type === "textarea" || f.type === "tags"
                              ? "sm:col-span-2"
                              : ""
                          }
                        >
                          <Label
                            data-required={f.required ? "true" : undefined}
                          >
                            {f.label}
                          </Label>
                          {f.lookup ? (
                            <Select
                              value={(editing as any)?.[f.name] ?? ""}
                              onValueChange={v =>
                                setEditing({
                                  ...(editing ?? {}),
                                  [f.name]: v,
                                } as any)
                              }
                            >
                              <SelectTrigger className="bg-muted/40 rounded-xl border-transparent">
                                <SelectValue
                                  placeholder={
                                    f.placeholder ?? `Select ${f.label}...`
                                  }
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(lookups[f.name] ?? {})
                                  .sort(([, a], [, b]) =>
                                    a.label.localeCompare(b.label)
                                  )
                                  .map(([id, { label, secondary }]) => (
                                    <SelectItem key={id} value={id}>
                                      {label}
                                      {secondary ? ` — ${secondary}` : ""}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                          ) : f.type === "textarea" ? (
                            <Textarea
                              value={(editing as any)?.[f.name] ?? ""}
                              onChange={e =>
                                setEditing({
                                  ...(editing ?? {}),
                                  [f.name]: e.target.value,
                                } as any)
                              }
                              rows={3}
                              className="bg-muted/40 rounded-xl border-transparent"
                            />
                          ) : f.type === "select" ? (
                            <Select
                              value={(editing as any)?.[f.name] ?? ""}
                              onValueChange={v =>
                                setEditing({
                                  ...(editing ?? {}),
                                  [f.name]: v,
                                } as any)
                              }
                            >
                              <SelectTrigger className="bg-muted/40 rounded-xl border-transparent">
                                <SelectValue
                                  placeholder={f.placeholder ?? "Select..."}
                                />
                              </SelectTrigger>
                              <SelectContent>
                                {f.options?.map(o => (
                                  <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : f.type === "boolean" ? (
                            <div className="bg-muted/40 flex h-11 items-center gap-3 rounded-xl px-3">
                              <Switch
                                checked={
                                  (editing as any)?.[f.name] === true ||
                                  (editing as any)?.[f.name] === "true"
                                }
                                onCheckedChange={(v: boolean) =>
                                  setEditing({
                                    ...(editing ?? {}),
                                    [f.name]: v,
                                  } as any)
                                }
                              />
                              <span className="text-sm text-muted-foreground">
                                {(editing as any)?.[f.name] === true ||
                                (editing as any)?.[f.name] === "true"
                                  ? "Yes"
                                  : "No"}
                              </span>
                            </div>
                          ) : f.type === "tags" ? (
                            <TagsInput
                              value={(editing as any)?.[f.name]}
                              onChange={(arr: (string | number)[]) =>
                                setEditing({
                                  ...(editing ?? {}),
                                  [f.name]: arr,
                                } as any)
                              }
                              placeholder={
                                f.placeholder ?? "Type and press Enter…"
                              }
                              isCurrency={
                                f.currency === true ||
                                f.name === "suggested_amounts"
                              }
                            />
                          ) : (
                            <Input
                              type={f.type ?? "text"}
                              required={f.required}
                              value={(editing as any)?.[f.name] ?? ""}
                              onChange={e =>
                                setEditing({
                                  ...(editing ?? {}),
                                  [f.name]: e.target.value,
                                } as any)
                              }
                              placeholder={f.placeholder}
                              className="bg-muted/40 rounded-xl border-transparent"
                            />
                          )}
                          {f.help && (
                            <p className="mt-1 text-[11px] text-muted-foreground">
                              {f.help}
                            </p>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
                <DialogFooter className="shrink-0 border-t border-border bg-background px-4 py-3 sm:px-6 sm:py-4">
                  <Button type="submit" className="w-full rounded-xl sm:w-auto">
                    Save
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="-mx-3 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm sm:mx-0">
        <Table className="[&_td]:border-border/70 min-w-[640px] border-collapse [&_td]:border [&_th]:border [&_th]:border-border">
          <TableHeader className="bg-muted/60">
            <TableRow className="hover:bg-muted/60">
              {tableFields.map(f => (
                <TableHead
                  key={f.name}
                  className="px-3 py-2.5 font-semibold text-foreground"
                >
                  {f.label}
                </TableHead>
              ))}
              <TableHead className="w-32 px-3 py-2.5 text-right font-semibold text-foreground">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={tableFields.length + 1}
                  className="py-10 text-center text-muted-foreground"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={tableFields.length + 1}
                  className="py-10 text-center text-muted-foreground"
                >
                  No records yet.
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((r, i) => (
                <TableRow
                  key={r.id}
                  className={i % 2 === 0 ? "bg-background" : "bg-muted/20"}
                >
                  {tableFields.map(f => {
                    const v = (r as any)[f.name];
                    let display: any;
                    if (f.lookup) {
                      const hit = v ? lookups[f.name]?.[v] : null;
                      display = hit
                        ? hit.secondary
                          ? `${hit.label} (${hit.secondary})`
                          : hit.label
                        : v
                          ? "—"
                          : "—";
                    } else if (f.type === "boolean") {
                      display =
                        v === true ? (
                          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                            Yes
                          </Badge>
                        ) : (
                          <Badge variant="secondary">No</Badge>
                        );
                    } else if (f.type === "tags") {
                      const arr = Array.isArray(v) ? v : [];
                      display = arr.length ? (
                        <div className="flex flex-wrap gap-1.5">
                          {arr.map((t: any, idx: number) => (
                            <span
                              key={`tag-${idx}-${String(t)}`}
                              className="rounded-md border border-amber-100/50 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900"
                            >
                              {f.currency === true ||
                              f.name === "suggested_amounts"
                                ? `₹${isNaN(Number(t)) ? t : Number(t).toLocaleString("en-IN")}`
                                : t}
                            </span>
                          ))}
                        </div>
                      ) : (
                        "—"
                      );
                    } else if (
                      f.type === "date" ||
                      /(_date|_at)$/.test(f.name)
                    ) {
                      display = v ? formatDate(v) : "—";
                    } else {
                      display = v ?? "—";
                    }
                    return (
                      <TableCell
                        key={f.name}
                        className="max-w-xs truncate px-3 py-2.5"
                      >
                        {statusOptions?.includes(f.name) ||
                        f.name === "status" ? (
                          <Badge variant="secondary" className="capitalize">
                            {v ?? "—"}
                          </Badge>
                        ) : (
                          display
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell className="px-3 py-2.5 text-right">
                    <div className="flex justify-end gap-1">
                      {renderRowExtra?.(r)}
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => startEdit(r)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => remove(r.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <div className="text-xs text-muted-foreground">
        Showing {visibleRows.length} of {filtered.length} record
        {filtered.length === 1 ? "" : "s"}
      </div>
    </div>
  );
}
